/**
 * BulkHistoryPanel - Bulk Operation History
 *
 * FERP CRITICAL: Display history of all bulk operations for tenant
 *
 * Features:
 * - Table of previous bulk operations
 * - Filter by status (all/completed/failed)
 * - Click row to view details
 * - Download results CSV
 * - Rollback capability (if within window)
 * - Refresh button
 *
 * Architecture Principles:
 * - Data-driven (history from BulkOperationRepository)
 * - Repository pattern
 * - Multi-tenant isolation
 * - Immutable audit trails
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Stack,
  Title,
  Table,
  Badge,
  Button,
  Group,
  Select,
  Alert,
  Card,
  ActionIcon,
  Tooltip,
  Loader,
  Text,
  Box,
} from '@mantine/core';
import {
  IconRefresh,
  IconEye,
  IconDownload,
  IconArrowBackUp,
  IconClock,
  IconCheck,
  IconX,
  IconAlertCircle,
} from '@tabler/icons-react';
import { useApolloClient } from '@apollo/client/react';
import { ApolloClient } from '@apollo/client';
import {
  BulkOperation,
  BulkOperationRepository,
  BatchStatus,
} from '../data/repositories/BulkOperationRepository';

export interface BulkHistoryPanelProps {
  /** Tenant ID for multi-tenant isolation */
  tenantId: string;
  /** Callback when operation is selected for viewing */
  onViewDetails?: (operation: BulkOperation) => void;
  /** Callback when rollback is requested */
  onRollback?: (batchId: string, reason: string) => void;
}

/**
 * Get status color
 */
function getStatusColor(status: BatchStatus): string {
  switch (status) {
    case BatchStatus.PENDING:
      return 'gray';
    case BatchStatus.PROCESSING:
      return 'blue';
    case BatchStatus.COMPLETED:
      return 'green';
    case BatchStatus.FAILED:
      return 'red';
    case BatchStatus.CANCELLED:
      return 'orange';
    default:
      return 'gray';
  }
}

/**
 * Get status icon
 */
function getStatusIcon(status: BatchStatus) {
  switch (status) {
    case BatchStatus.PENDING:
      return <IconClock size={14} />;
    case BatchStatus.PROCESSING:
      return <IconClock size={14} />;
    case BatchStatus.COMPLETED:
      return <IconCheck size={14} />;
    case BatchStatus.FAILED:
      return <IconX size={14} />;
    case BatchStatus.CANCELLED:
      return <IconAlertCircle size={14} />;
    default:
      return null;
  }
}

/**
 * Format date
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString();
}

/**
 * Calculate duration
 */
function calculateDuration(startedAt: string, completedAt?: string): string {
  if (!completedAt) return 'In progress';

  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  const durationMs = end - start;
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);

  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

/**
 * Bulk History Panel
 *
 * FERP Architecture Compliance:
 * - ✅ Data-driven (history from BulkOperationRepository)
 * - ✅ Repository pattern
 * - ✅ Multi-tenant isolation
 * - ✅ Immutable audit trails
 */
export function BulkHistoryPanel({
  tenantId,
  onViewDetails,
  onRollback,
}: BulkHistoryPanelProps) {
  const apolloClient = useApolloClient() as ApolloClient;
  const repository = useMemo(() => new BulkOperationRepository(apolloClient), [apolloClient]);

  // State
  const [operations, setOperations] = useState<BulkOperation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  /**
   * Load operations
   */
  const loadOperations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await repository.listBulkOperations(tenantId, 50); // Limit to 50
      setOperations(result);
    } catch (err) {
      console.error('[BulkHistoryPanel] Failed to load operations:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [tenantId, repository]);

  /**
   * Load operations on mount
   */
  useEffect(() => {
    loadOperations();
  }, [loadOperations]);

  /**
   * Filter operations by status
   */
  const filteredOperations = useMemo(() => {
    if (filterStatus === 'all') {
      return operations;
    }
    return operations.filter(op => op.status === filterStatus);
  }, [operations, filterStatus]);

  /**
   * Handle view details
   */
  const handleViewDetails = useCallback(async (batchId: string) => {
    if (!onViewDetails) return;

    try {
      const operation = await repository.getBulkOperationStatus(batchId, tenantId);
      if (operation) {
        onViewDetails(operation);
      }
    } catch (err) {
      console.error('[BulkHistoryPanel] Failed to fetch operation details:', err);
    }
  }, [repository, tenantId, onViewDetails]);

  // Loading state
  if (loading && operations.length === 0) {
    return (
      <Card withBorder>
        <Group justify="center" p="xl">
          <Loader size="lg" />
          <Text>Loading operation history...</Text>
        </Group>
      </Card>
    );
  }

  // Error state
  if (error && operations.length === 0) {
    return (
      <Alert color="red" title="Error" icon={<IconAlertCircle />}>
        Failed to load operation history: {error.message}
        <Button
          variant="subtle"
          size="sm"
          onClick={loadOperations}
          mt="md"
        >
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" ta="center">
            <Title order={4}>Bulk Operation History</Title>
            <Group>
              <Select
                value={filterStatus}
                onChange={(value) => setFilterStatus(value || 'all')}
                data={[
                  { value: 'all', label: 'All' },
                  { value: BatchStatus.COMPLETED, label: 'Completed' },
                  { value: BatchStatus.FAILED, label: 'Failed' },
                  { value: BatchStatus.PROCESSING, label: 'Processing' },
                  { value: BatchStatus.CANCELLED, label: 'Cancelled' },
                ]}
                placeholder="Filter by status"
                w={150}
              />
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={loadOperations}
                loading={loading}
              >
                Refresh
              </Button>
            </Group>
          </Group>

          {filteredOperations.length === 0 ? (
            <Alert color="blue" icon={<IconAlertCircle />}>
              No bulk operations found
              {filterStatus !== 'all' && ' with this status'}.
            </Alert>
          ) : (
            <Box style={{ overflowX: 'auto' }}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>File</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Total</Table.Th>
                    <Table.Th>Success</Table.Th>
                    <Table.Th>Failed</Table.Th>
                    <Table.Th>Duration</Table.Th>
                    <Table.Th>Started</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredOperations.map((operation) => {
                    const successRate = operation.totalRows > 0
                      ? Math.round((operation.successCount / operation.totalRows) * 100)
                      : 0;

                    return (
                      <Table.Tr key={operation.batchId}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {operation.filename}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {operation.processedBy}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={getStatusColor(operation.status)}
                            leftSection={getStatusIcon(operation.status)}
                          >
                            {operation.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{operation.totalRows}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="green" fw={500}>
                            {operation.successCount}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="red" fw={500}>
                            {operation.failureCount}
                          </Text>
                          {successRate < 100 && (
                            <Text size="xs" c="dimmed">
                              ({successRate}% success)
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {calculateDuration(operation.startedAt, operation.completedAt)}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {formatDate(operation.startedAt)}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="View details">
                              <ActionIcon
                                variant="light"
                                onClick={() => handleViewDetails(operation.batchId)}
                              >
                                <IconEye size={16} />
                              </ActionIcon>
                            </Tooltip>

                            {operation.isRollbackable && onRollback && (
                              <Tooltip label="Rollback (24h window)">
                                <ActionIcon
                                  variant="light"
                                  color="orange"
                                  onClick={() => {
                                    const reason = prompt('Enter rollback reason:');
                                    if (reason) {
                                      onRollback(operation.batchId, reason);
                                    }
                                  }}
                                >
                                  <IconArrowBackUp size={16} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Box>
          )}

          {filteredOperations.length > 0 && (
            <Text size="xs" c="dimmed" ta="right">
              Showing {filteredOperations.length} operation(s)
            </Text>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}

export default BulkHistoryPanel;
